import { getCurrentUser, signOut, fetchUserAttributes, updateUserAttributes } from 'aws-amplify/auth';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import config from '../config';
import '../styles/account.css';
import {FaPencil} from 'react-icons/fa6';


function Account() {
    const [user, setUser] = useState(null);
    const [orders, setOrders] = useState([]);
    const [tab, setTab] = useState('profile'); // 'profile' or 'orders'
    const [isEditing, setIsEditing] = useState(false);
    const [editEmail, setEditEmail] = useState('');
    const [editFirstName, setEditFirstName] = useState('');
    const [editLastName, setEditLastName] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const host = config.backendUrl;

    const navigate = useNavigate();

    useEffect(() => {
        const checkUser = async () => {
            try {
                const user = await getCurrentUser();
                if (!user) {
                    navigate('/login');
                }
                const attributes = await fetchUserAttributes(user);
                console.log('User attributes:', attributes);
                setUser(attributes);
                // Pre-fill edit fields
                setEditEmail(attributes.email || '');
                setEditFirstName(attributes.given_name || '');
                setEditLastName(attributes.family_name || '');
            } catch (error) {
                console.error('Error fetching user:', error);
                navigate('/login');
            }
        }

        checkUser();
    }, []);


    useEffect(() => {
        const fetchOrders = async () => {
            try {
                const user = await getCurrentUser();
                if (user) {
                    console.log('Fetching orders for user:', user.username);
                    console.log(`Host URL: ${host}`);
                    const response = await fetch(`${host}/api/orders/${user.username}`, {
                        headers: {
                            "X-API-Key": process.env.REACT_APP_GANDF_KEY,
                            "Content-Type": "application/json"
                        }
                    });
                    if (!response.ok) {
                        throw new Error('Failed to fetch orders');
                    }
                    const data = await response.json();
                    setOrders(data.orders || []);
                }
            } catch (error) {
                console.error('Error fetching orders:', error);
            }
        }
        if (user) {
            fetchOrders();
        }
    }, [user]);

        
    const handleSignOut = async () => {
        try {
            await signOut();
            navigate('/login');
        } catch (error) {
            console.error('Error signing out:', error);
        }
    }

    const handleEdit = () => {
        setIsEditing(true);
        setError('');
    };

    const handleCancel = () => {
        setIsEditing(false);
        setError('');
        // Reset fields to current user values
        if (user) {
            setEditEmail(user.email || '');
            setEditFirstName(user.given_name || '');
            setEditLastName(user.family_name || '');
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');
        try {
            // Correct usage for Amplify v6: no user argument, use named param
            await updateUserAttributes({
                userAttributes: {
                    email: editEmail,
                    given_name: editFirstName,
                    family_name: editLastName
                }
            });
            // Refetch attributes to update UI
            const updatedAttributes = await fetchUserAttributes();
            setUser(updatedAttributes);
            setIsEditing(false);
        } catch (err) {
            setError('Failed to update profile.');
            console.error('Update error:', err);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="account">
            <div className="account-header">
                <div className="account-header-text">
                    <h1>My Account</h1>
                    <p>Manage your account settings and view your order history.</p>
                </div>
                <button onClick={handleSignOut} className="sign-out-button">Sign Out</button>
            </div>

            <div className="account-tabber">
                <button onClick={() => setTab("profile")} className={`account-tab ${tab === 'profile' ? 'active' : ''}`}>Profile</button>
                <button onClick={() => setTab("orders")} className={`account-tab ${tab === 'orders' ? 'active' : ''}`}>Orders</button>
            </div>

            {tab === 'profile' && (
                <div className="account-profile">
                    <div className="profile-header">
                        <h2>Profile Information</h2>
                        {!isEditing && (
                            <button className="edit-profile-button" onClick={handleEdit}>Edit</button>
                        )}
                    </div>
                    {error && <div className="profile-error">{error}</div>}
                    {isEditing ? (
                        <form className="profile-edit-form" onSubmit={handleSave}>
                            <label>
                                Email:
                                <input
                                    type="email"
                                    value={editEmail}
                                    onChange={e => setEditEmail(e.target.value)}
                                    required
                                />
                            </label>
                            <label>
                                First Name:
                                <input
                                    type="text"
                                    value={editFirstName}
                                    onChange={e => setEditFirstName(e.target.value)}
                                />
                            </label>
                            <label>
                                Last Name:
                                <input
                                    type="text"
                                    value={editLastName}
                                    onChange={e => setEditLastName(e.target.value)}
                                />
                            </label>
                            <div className="profile-edit-actions">
                                <button type="submit" className="save-profile-button" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
                                <button type="button" className="cancel-profile-button" onClick={handleCancel} disabled={saving}>Cancel</button>
                            </div>
                        </form>
                    ) : user ? (
                        <div className="profile-info">
                            <p><strong>Email:</strong> {user.email}</p>
                            <p><strong>First Name:</strong> {user.given_name || 'N/A'}</p>
                            <p><strong>Last Name:</strong> {user.family_name || 'N/A'}</p>
                        </div>
                    ) : (
                        <p>Loading profile information...</p>
                    )}
                </div>
            )}

            {tab === 'orders' && (
                <div className="account-orders">
                    <h2>Order History</h2>
                    {orders.length > 0 ? (
                        <ul className="order-list">
                            {orders.map((order) => (
                                <li key={order.id} className="order-item">
                                    <p><strong>Order ID:</strong> 
                                        <button 
                                            onClick={() => navigate(`/order?tracking_token=${order.tracking_token}`)}
                                            className="order-link"
                                        >
                                            {order.id}
                                        </button>
                                    </p>
                                    <p><strong>Date:</strong> {new Date(order.timestamp * 1000).toLocaleDateString()}</p>
                                    <p><strong>Total:</strong> ${(order.cost.total / 100).toFixed(2)}</p>
                                    <p><strong>Items:</strong> {order.items ? order.items.reduce((sum, item) => sum + item.quantity, 0) : 0}</p>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p>No orders found.</p>
                    )}
                </div>
            )}     
        </div>
    );

}

export default Account;


